'use client';

import { Box, BoxProps, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import React, { ComponentPropsWithoutRef, useMemo, useState } from 'react';

import { cn } from '../../lib';
import { StyledInput } from './styled';

export interface InputProps extends ComponentPropsWithoutRef<typeof TextField> {
  width?: number;
  style?: BoxProps['style'];
  className?: string;
  inputPd?: string | number;
  bgColor?: string;
  inputColor?: string;
  inputHeight?: string | number;
  inputFontSz?: string | number;
  borderColor?: string;
  focusedBorderColor?: string;
  isMultiline?: boolean;
  isFullWidth?: boolean;
  isNeedFocus?: boolean;
  onBlur?: TextFieldProps['onBlur'];
  onFocus?: TextFieldProps['onFocus'];
  startIcon?: React.ReactNode;
  readOnly?: boolean;
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      sx,
      width,
      style,
      bgColor,
      className,
      inputHeight = '40px',
      inputFontSz = '14px',
      inputColor = 'white',
      inputPd = '8px 16px',
      borderColor = '#FFFFFF26',
      focusedBorderColor,
      isFullWidth = true,
      isNeedFocus = true,
      isMultiline = false,
      onBlur,
      onFocus,
      startIcon,
      readOnly,
      ...textFieldProps
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const border = useMemo(() => {
      if (!borderColor) return 'none';
      const color = isFocused && isNeedFocus ? focusedBorderColor || 'white' : borderColor;

      return `1px solid ${color}`;
    }, [borderColor, isNeedFocus, isFocused]);

    return (
      <StyledInput
        className={cn(className)}
        style={{
          width: width ?? (isFullWidth ? 'inherit' : 'fit-content'),
          ...style,
        }}>
        <Box tabIndex={-1} position={'relative'} height={inputHeight} boxSizing={'content-box'}>
          <TextField
            inputRef={ref}
            variant={'filled'}
            multiline={isMultiline}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setIsFocused(true);
              onFocus?.(event);
            }}
            onBlur={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setIsFocused(false);
              onBlur?.(event);
            }}
            slotProps={{
              input: {
                startAdornment: startIcon,
                readOnly: readOnly,
              },
            }}
            sx={{
              '&.MuiFormControl-root': {
                border,
                backgroundColor: bgColor,
              },
              '& .MuiInputBase-input': {
                color: inputColor,
                fontSize: inputFontSz,
              },
              '& .MuiFilledInput-input': {
                padding: inputPd,
              },
              ...sx,
            }}
            {...textFieldProps}
          />
        </Box>
      </StyledInput>
    );
  },
);

Input.displayName = 'Input';

export { Input };
